import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Button, Spinner, Form } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { 
  FaChartLine, 
  FaDollarSign, 
  FaShoppingCart, 
  FaEye, 
  FaBox, 
  FaUsers, 
  FaCalendarAlt,
  FaArrowUp,
  FaArrowDown,
  FaDownload,
  FaFilter,
  FaTrendingUp,
  FaTrendingDown,
  FaClock,
  FaCheckCircle
} from 'react-icons/fa';
import Layout from '../../components/layout/Layout';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import './Analytics.css';

const AnalyticsPage = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('30');
  
  const [analytics, setAnalytics] = useState({
    totalRevenue: 0,
    totalOrders: 0,
    totalViews: 0,
    conversionRate: 0,
    avgOrderValue: 0,
    topProducts: [],
    recentOrders: [],
    revenueGrowth: 0,
    ordersGrowth: 0
  });

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await api.get(`/users/seller/analytics/?days=${timeRange}`);
      const data = response.data;
      
      setAnalytics({
        totalRevenue: data.totalRevenue,
        totalOrders: data.totalOrders,
        totalViews: data.totalViews,
        conversionRate: data.conversionRate,
        avgOrderValue: data.avgOrderValue,
        revenueGrowth: data.revenueGrowth,
        ordersGrowth: data.ordersGrowth,
        topProducts: data.topProducts || [],
        recentOrders: data.recentOrders || []
      });
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: 'GHS'
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  if (loading) {
    return (
      <Layout>
        <div className="seller-analytics-container">
          <Container className="py-4">
            <div className="text-center">
              <Spinner animation="border" className="text-primary" />
              <p className="mt-3">Loading analytics...</p>
            </div>
          </Container>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="seller-analytics-container">
        <Container className="py-4">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="analytics-header mb-4"
          >
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1 className="analytics-title">Analytics</h1>
                <p className="analytics-subtitle">Track your sales performance and customer insights</p>
              </div>
              <div className="d-flex gap-3">
                <Form.Select 
                  value={timeRange} 
                  onChange={(e) => setTimeRange(e.target.value)}
                  className="time-range-select"
                >
                  <option value="7">Last 7 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="90">Last 90 days</option>
                  <option value="365">Last year</option>
                </Form.Select>
                <Button variant="outline-primary" className="export-btn">
                  <FaDownload className="me-2" /> Export
                </Button>
              </div>
            </div>
          </motion.div>

          {error && <div className="alert alert-danger">{error}</div>}

          {/* Stats Cards */}
          <Row className="mb-4">
            <Col lg={3} md={6} className="mb-3">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 }}
              >
                <Card className="analytics-stat-card revenue-card">
                  <Card.Body>
                    <div className="stat-header">
                      <div className="stat-icon-wrapper">
                        <FaDollarSign />
                      </div>
                      <div className={`stat-trend ${analytics.revenueGrowth >= 0 ? 'positive' : 'negative'}`}>
                        <FaArrowUp />
                        {Math.abs(analytics.revenueGrowth)}%
                      </div>
                    </div>
                    <div className="stat-content">
                      <div className="stat-value">{formatCurrency(analytics.totalRevenue)}</div>
                      <div className="stat-label">Total Revenue</div>
                    </div>
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>

            <Col lg={3} md={6} className="mb-3">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
              >
                <Card className="analytics-stat-card orders-card">
                  <Card.Body>
                    <div className="stat-header">
                      <div className="stat-icon-wrapper">
                        <FaShoppingCart />
                      </div>
                      <div className={`stat-trend ${analytics.ordersGrowth >= 0 ? 'positive' : 'negative'}`}>
                        <FaArrowUp />
                        {Math.abs(analytics.ordersGrowth)}%
                      </div>
                    </div>
                    <div className="stat-content">
                      <div className="stat-value">{formatNumber(analytics.totalOrders)}</div>
                      <div className="stat-label">Total Orders</div>
                    </div>
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>

            <Col lg={3} md={6} className="mb-3">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
              >
                <Card className="analytics-stat-card views-card">
                  <Card.Body>
                    <div className="stat-header">
                      <div className="stat-icon-wrapper">
                        <FaEye />
                      </div>
                      <div className="stat-trend positive">
                        <FaArrowUp />
                        15.2%
                      </div>
                    </div>
                    <div className="stat-content">
                      <div className="stat-value">{formatNumber(analytics.totalViews)}</div>
                      <div className="stat-label">Total Views</div>
                    </div>
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>

            <Col lg={3} md={6} className="mb-3">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 }}
              >
                <Card className="analytics-stat-card conversion-card">
                  <Card.Body>
                    <div className="stat-header">
                      <div className="stat-icon-wrapper">
                        <FaChartLine />
                      </div>
                      <div className="stat-trend positive">
                        <FaArrowUp />
                        3.1%
                      </div>
                    </div>
                    <div className="stat-content">
                      <div className="stat-value">{analytics.conversionRate}%</div>
                      <div className="stat-label">Conversion Rate</div>
                    </div>
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>
          </Row>

          {/* Charts and Tables */}
          <Row>
            <Col lg={8} className="mb-4">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
              >
                <Card className="analytics-card glass-card">
                  <Card.Header className="glass-card-header">
                    <h5 className="mb-0">Revenue Overview</h5>
                    <Badge bg="primary">Last {timeRange} days</Badge>
                  </Card.Header>
                  <Card.Body>
                    <div className="chart-placeholder">
                      <div className="text-center py-5">
                        <FaChartLine className="chart-icon" />
                        <p className="text-muted">Revenue chart will be displayed here</p>
                        <small className="text-muted">Integration with chart library needed</small>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>

            <Col lg={4} className="mb-4">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 }}
              >
                <Card className="analytics-card glass-card">
                  <Card.Header className="glass-card-header">
                    <h5 className="mb-0">Top Products</h5>
                  </Card.Header>
                  <Card.Body>
                    <div className="top-products-list">
                      {analytics.topProducts.map((product, index) => (
                        <div key={index} className="top-product-item">
                          <div className="product-rank">{index + 1}</div>
                          <div className="product-info">
                            <div className="product-name">{product.name}</div>
                            <div className="product-stats">
                              <span className="sales-count">{product.sales} sales</span>
                              <span className="revenue-amount">{formatCurrency(product.revenue)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>
          </Row>

          {/* Recent Orders Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <Card className="analytics-card glass-card">
              <Card.Header className="glass-card-header">
                <h5 className="mb-0">Recent Orders</h5>
                <Button variant="link" className="view-all-btn">View All</Button>
              </Card.Header>
              <Card.Body>
                <div className="orders-table">
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>Order ID</th>
                          <th>Customer</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.recentOrders.map((order, index) => (
                          <tr key={index}>
                            <td className="order-id">{order.id}</td>
                            <td>{order.customer}</td>
                            <td className="order-amount">{formatCurrency(order.amount)}</td>
                            <td>
                              <Badge 
                                bg={
                                  order.status === 'completed' ? 'success' :
                                  order.status === 'processing' ? 'warning' : 'info'
                                }
                                className="status-badge"
                              >
                                {order.status}
                              </Badge>
                            </td>
                            <td>{order.date}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </motion.div>
        </Container>
      </div>
    </Layout>
  );
};

export default AnalyticsPage;
